import { useCallback } from "react";

import { Action, Context } from "types/generated";
import {
  AccessResult,
  hasAtLeastOneRequiredAction,
  hasManageAccess,
  NO_ACCESS_MESSAGE,
} from "utils/access";
import useAacFlags from "hooks/useAacFlags";
import { hasSpaceManageAccess } from "utils/user";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";

const DELETE_CONTEXT_ACCESS_TYPES_V2 = [Action.SpaceAdmin, Action.ContextDelete];

const useCanDeleteContext = (): ((context: Context) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const { customAccessRolesV2 } = useTypedFlags();

  const { customAccessRoles } = useAacFlags();

  return useCallback(
    (context: Context) => {
      if (viewer.admin) {
        return { result: true };
      }

      const spaceId = context.space || context.spaceDetails.id;

      let hasAccess = false;

      if (customAccessRolesV2) {
        hasAccess = hasAtLeastOneRequiredAction(DELETE_CONTEXT_ACCESS_TYPES_V2, viewer, spaceId);
      } else if (customAccessRoles) {
        hasAccess = hasManageAccess(viewer, spaceId);
      } else {
        hasAccess = hasSpaceManageAccess(context.spaceDetails.accessLevel);
      }

      if (!hasAccess) {
        return { result: false, message: NO_ACCESS_MESSAGE };
      }

      return { result: true };
    },
    [customAccessRoles, customAccessRolesV2, viewer]
  );
};

export default useCanDeleteContext;
